/*
  Export-Modell für den Schichtplan V2.

  Baut aus den Plan-Daten eine Zeilen-x-Tage-Matrix, die PDF-Export,
  Excel-Export und die Dialog-Vorschau gemeinsam nutzen.

  Was in einer Zelle steht, steuern die granularen `cellDisplay`-Optionen
  (Zeit, Abteilung, Schicht, Abkürzungen). Jeder Eintrag liefert daraus
  fertige `time`- und `name`-Strings, die alle Renderer identisch anzeigen.

  Dazu kommt `code`, das Kürzel des Eintrags. In dichten Rastern (Monat) ist
  eine Tagesspalte nur rund 20 px breit – dort steht in der Zelle nur noch
  Farbe und Kürzel, und die Legende sagt, wofür beides steht.
*/

import { getShiftDayKeys } from "./selectors";
import { getUserHolidayDayKeys } from "./userHolidays";

export const EXPORT_ENTRY_KINDS = Object.freeze({
  SHIFT: "shift",
  ABSENCE: "absence",
  HOLIDAY: "holiday",
});

// = --ds-color-category-08-default (wie --spv2-vacation im Raster).
export const HOLIDAY_ENTRY_COLOR = "#7B61C9";

export const DEFAULT_CELL_DISPLAY = Object.freeze({
  showTime: true,
  showDepartment: true,
  showShift: false,
  // Ausgeschrieben: Abteilungskürzel wie "KÜ" sind für das Team oft kryptisch.
  useAbbreviations: false,
});

const MAX_CODE_LENGTH = 3;

// Reihenfolge der Legende: erst die Arbeit, dann was sie ersetzt.
const LEGEND_KIND_ORDER = Object.freeze({
  [EXPORT_ENTRY_KINDS.SHIFT]: 0,
  [EXPORT_ENTRY_KINDS.ABSENCE]: 1,
  [EXPORT_ENTRY_KINDS.HOLIDAY]: 2,
});

const DEFAULT_OPTIONS = Object.freeze({
  includeAbsences: true,
  includeHolidays: true,
  releasedOnly: false,
  employeeIds: null,
  cellDisplay: DEFAULT_CELL_DISPLAY,
});

function normalizeText(value) {
  return String(value || "").trim();
}

function isWorkShiftEntry(entry) {
  return entry?.entryType === "shift" || entry?.entryType === "custom_shift";
}

function compareByStart(left, right) {
  const startCompare = String(left?.start || "").localeCompare(String(right?.start || ""));
  if (startCompare !== 0) return startCompare;
  return String(left?.title || "").localeCompare(String(right?.title || ""), "de", { sensitivity: "base" });
}

function pickName(shortName, fullName, useAbbreviations) {
  const short = normalizeText(shortName);
  const full = normalizeText(fullName);
  if (useAbbreviations) return short || full;
  return full || short;
}

/*
  Kürzel für die enge Zelle: bevorzugt das gepflegte Kurzzeichen. Fehlt es,
  wird eines gebildet – aus mehrteiligen Namen die Anfangsbuchstaben
  ("Früh Service" → "FS"), aus einteiligen der Wortanfang ("Ferien" → "Fer").
*/
function buildEntryCode(shortName, fullName) {
  const short = normalizeText(shortName);
  if (short) return short.slice(0, MAX_CODE_LENGTH);

  const full = normalizeText(fullName);
  if (!full) return "";

  const words = full.split(/[\s·/-]+/).filter(Boolean);
  if (words.length > 1) {
    return words
      .slice(0, MAX_CODE_LENGTH)
      .map((word) => word[0])
      .join("")
      .toLocaleUpperCase("de");
  }

  return full.slice(0, MAX_CODE_LENGTH);
}

function buildShiftName(shift, cellDisplay) {
  const department = pickName(shift.departmentShortName, shift.department, cellDisplay.useAbbreviations);
  const shiftName = pickName(shift.titleShort, shift.title, cellDisplay.useAbbreviations);

  const parts = [];
  if (cellDisplay.showDepartment && department) parts.push(department);
  if (cellDisplay.showShift && shiftName) parts.push(shiftName);

  // Sind beide Teile abgewählt (oder leer), bleibt wenigstens ein Bezeichner stehen.
  if (!parts.length) return shiftName || department || "Schicht";
  return parts.join(" · ");
}

function buildShiftCellEntry(shift, cellDisplay) {
  return {
    kind: EXPORT_ENTRY_KINDS.SHIFT,
    entryId: String(shift.id),
    time: cellDisplay.showTime ? normalizeText(shift.timeLabel) : "",
    name: buildShiftName(shift, cellDisplay),
    // Das Kürzel meint die Schicht; erst wenn die keinen Namen hat, die Abteilung.
    code: buildEntryCode(shift.titleShort, shift.title) || buildEntryCode(shift.departmentShortName, shift.department),
    timeLabel: normalizeText(shift.timeLabel),
    fullLabel: normalizeText(shift.title) || normalizeText(shift.department) || "Schicht",
    color: normalizeText(shift.color),
    isDraft: Boolean(shift.isDraft),
    start: shift.start || "",
  };
}

function buildAbsenceCellEntry(absence, cellDisplay) {
  return {
    kind: EXPORT_ENTRY_KINDS.ABSENCE,
    entryId: String(absence.id),
    time: cellDisplay.showTime ? normalizeText(absence.timeLabel) : "",
    name: pickName(absence.titleShort, absence.title, cellDisplay.useAbbreviations) || "Absenz",
    code: buildEntryCode(absence.titleShort, absence.title),
    timeLabel: normalizeText(absence.timeLabel),
    fullLabel: normalizeText(absence.title) || "Absenz",
    color: normalizeText(absence.color),
    isDraft: false,
    start: absence.start || "",
  };
}

function buildHolidayCellEntry(holiday, cellDisplay) {
  const name = normalizeText(holiday.holidayTypeName) || "Ferien";
  return {
    kind: EXPORT_ENTRY_KINDS.HOLIDAY,
    entryId: String(holiday.id),
    time: cellDisplay.showTime ? "Ganztag" : "",
    name,
    // Ferientypen führen kein Kurzzeichen – das Kürzel entsteht aus dem Namen.
    code: buildEntryCode("", name),
    timeLabel: "Ganztag",
    fullLabel: name,
    color: HOLIDAY_ENTRY_COLOR,
    isDraft: false,
    start: `${holiday.startDate || ""}T00:00:00`,
  };
}

function addCellEntry(cellsByDayKey, dayKey, entry) {
  if (!cellsByDayKey[dayKey]) cellsByDayKey[dayKey] = [];
  cellsByDayKey[dayKey].push(entry);
}

/*
  Die Legende erklärt Farbe und Kürzel der Zellen – im Monatsraster steht dort
  sonst nichts mehr. Sie führt darum jede Art von Eintrag, nicht nur Schichten.

  Schichten werden nach Name und Zeit gruppiert (Früh und Spät sind zwei
  Einträge), Absenzen und Ferien nur nach Name: dort zählt die Art, und ihre
  Zeiten wären sonst eine Zeile pro Mitarbeitendem.
*/
function buildLegend(legendEntries) {
  const legendByKey = new Map();

  legendEntries.forEach((entry) => {
    const isShift = entry.kind === EXPORT_ENTRY_KINDS.SHIFT;
    const timeLabel = isShift ? entry.timeLabel : "";
    const key = `${entry.kind}::${entry.name.toLocaleLowerCase("de")}::${timeLabel}`;
    const current = legendByKey.get(key);

    if (current) {
      current.count += 1;
      return;
    }

    legendByKey.set(key, {
      key,
      kind: entry.kind,
      name: entry.name,
      code: entry.code,
      department: entry.department || "",
      timeLabel,
      color: entry.color,
      count: 1,
    });
  });

  return Array.from(legendByKey.values()).sort((left, right) => {
    const kindOrder = LEGEND_KIND_ORDER[left.kind] - LEGEND_KIND_ORDER[right.kind];
    if (kindOrder !== 0) return kindOrder;
    return left.name.localeCompare(right.name, "de", { sensitivity: "base", numeric: true });
  });
}

function toLegendEntry(cellEntry, department = "") {
  return {
    kind: cellEntry.kind,
    name: cellEntry.fullLabel,
    code: cellEntry.code,
    department,
    timeLabel: cellEntry.timeLabel,
    color: cellEntry.color,
  };
}

export function buildShiftplanExportModel({
  employees,
  shifts,
  userHolidays,
  days,
  publicHolidayNamesByDate = {},
  options = {},
} = {}) {
  const settings = { ...DEFAULT_OPTIONS, ...options };
  const cellDisplay = { ...DEFAULT_CELL_DISPLAY, ...(options.cellDisplay || {}) };
  const visibleDays = (Array.isArray(days) ? days : []).filter((day) => day?.key);
  const visibleDayKeys = new Set(visibleDays.map((day) => String(day.key)));
  const selectedEmployeeIds = Array.isArray(settings.employeeIds)
    ? new Set(settings.employeeIds.map((id) => String(id)))
    : null;

  const exportDays = visibleDays.map((day) => ({
    key: String(day.key),
    dayNumber: day.dayNumber,
    dayMonthLabel: day.dayMonthLabel || "",
    weekdayShort: day.weekdayShort || "",
    // Die Woche hat Platz für "Donnerstag" – das PDF schreibt ihn dort aus.
    weekdayLong: day.weekdayLong || "",
    isWeekend: Boolean(day.isWeekend),
    publicHolidayNames: Array.isArray(publicHolidayNamesByDate[day.key]) ? publicHolidayNamesByDate[day.key] : [],
  }));

  const rows = (Array.isArray(employees) ? employees : [])
    .filter((employee) => employee?.id && !employee.isPlaceholder)
    .filter((employee) => !selectedEmployeeIds || selectedEmployeeIds.has(String(employee.id)))
    .map((employee) => ({
      id: String(employee.id),
      name: normalizeText(employee.name) || "Unbenannt",
      cellsByDayKey: {},
    }));
  const rowsById = new Map(rows.map((row) => [row.id, row]));

  const stats = { shiftCount: 0, absenceCount: 0, holidayCount: 0, draftCount: 0 };
  const legendEntries = [];

  (Array.isArray(shifts) ? shifts : []).forEach((entry) => {
    const row = rowsById.get(String(entry?.employeeId || ""));
    if (!row || !entry?.id) return;

    if (isWorkShiftEntry(entry)) {
      if (settings.releasedOnly && entry.isDraft) return;

      const dayKeys = getShiftDayKeys(entry).filter((dayKey) => visibleDayKeys.has(dayKey));
      if (!dayKeys.length) return;

      stats.shiftCount += 1;
      if (entry.isDraft) stats.draftCount += 1;
      const cellEntry = buildShiftCellEntry(entry, cellDisplay);
      legendEntries.push(
        toLegendEntry(cellEntry, pickName(entry.departmentShortName, entry.department, cellDisplay.useAbbreviations))
      );
      dayKeys.forEach((dayKey) => addCellEntry(row.cellsByDayKey, dayKey, cellEntry));
      return;
    }

    if (entry.entryType === "absence" && settings.includeAbsences) {
      const dayKeys = getShiftDayKeys(entry).filter((dayKey) => visibleDayKeys.has(dayKey));
      if (!dayKeys.length) return;

      stats.absenceCount += 1;
      const cellEntry = buildAbsenceCellEntry(entry, cellDisplay);
      legendEntries.push(toLegendEntry(cellEntry));
      dayKeys.forEach((dayKey) => addCellEntry(row.cellsByDayKey, dayKey, cellEntry));
    }
  });

  if (settings.includeHolidays) {
    (Array.isArray(userHolidays) ? userHolidays : []).forEach((holiday) => {
      const row = rowsById.get(String(holiday?.userId || ""));
      if (!row || !holiday?.id) return;

      const dayKeys = getUserHolidayDayKeys(holiday).filter((dayKey) => visibleDayKeys.has(dayKey));
      if (!dayKeys.length) return;

      stats.holidayCount += 1;
      const cellEntry = buildHolidayCellEntry(holiday, cellDisplay);
      legendEntries.push(toLegendEntry(cellEntry));
      dayKeys.forEach((dayKey) => addCellEntry(row.cellsByDayKey, dayKey, cellEntry));
    });
  }

  rows.forEach((row) => {
    Object.values(row.cellsByDayKey).forEach((entries) => entries.sort(compareByStart));
  });

  return {
    days: exportDays,
    rows,
    stats,
    legend: buildLegend(legendEntries),
  };
}
